import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  FileIcon,
  FolderIcon,
  FolderInputIcon,
  Loader2Icon,
  PencilLineIcon,
  Trash2Icon,
} from 'lucide-react';

import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { ActionSheet } from '@/components/ui/phone/ActionSheet';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { FileEntry } from '@/lib/api/generated/model';
import { isProtectedFolder, protectionReason } from '../folders';
import { transitionName } from '../transitionName';
import { formatExactTime, formatFileSize, formatRelativeTime } from '../format';
import { DeleteFileDialog } from './DeleteFileDialog';
import { ParentDirectoryRow } from './ParentDirectoryRow';
import { useRowDragBinding } from '../fileDragContext';
import { canDrag } from '../moveTargets';
import { DeleteFolderDialog } from './DeleteFolderDialog';
import { useDeleteWorkspaceFile } from '../hooks/useDeleteWorkspaceFile';
import { useDeleteWorkspaceFolder } from '../hooks/useDeleteWorkspaceFolder';
import { useFolderContents } from '../hooks/useFolderContents';
import { classifyFileEntry } from '../fileClassification';
import { sortFileEntries } from '../sortFiles';
import type { FileSort, SortKey } from '../types';
import { useCanWork } from '@/features/workspaces/canWork';
import { useCoarsePointer } from '@/shared/hooks/useCoarsePointer';

/** A row's height; 44 on a touch screen, where the row and its ⋯ are targets (spec 272). */
const ROW_HEIGHT = 36;
const TOUCH_ROW_HEIGHT = 44;

/** How long a typed prefix keeps accumulating before it starts a new one. */
const TYPEAHEAD_WINDOW_MS = 800;

/**
 * `phone` false: left out below `sm` (spec 173). Kind, size, modified and the
 * three actions took 320 fixed pixels, more than a phone's whole row, and the
 * name was left none. Kind and size are the two the icon and the preview
 * already answer.
 */
const COLUMNS: { key: SortKey; label: string; width: string; phone?: false }[] =
  [
    { key: 'name', label: 'Name', width: 'flex-1' },
    // The listing was built without this column on the grounds that the
    // folder/file icon already carried the distinction. That held while the only
    // distinction was folder-versus-file; it does not hold for the eleven
    // readable kinds of spec 059, which the icon cannot express. The reversal is
    // deliberate — see specs/059-workspace-file-move-and-kind/spec.md.
    { key: 'kind', label: 'Kind', width: 'w-20', phone: false },
    { key: 'size', label: 'Size', width: 'w-16', phone: false },
    { key: 'modified_at', label: 'Modified', width: 'w-20' },
  ];

/** What a cell of a column left out on a phone carries. A row's actions show
 *  on hover; on a touch screen (`pointer-coarse`), which has none, they always
 *  show — an invisible button that still takes a tap is a trap. */
const NOT_ON_PHONE = 'max-sm:hidden';

/** Columns whose contents read as text rather than as a right-aligned figure. */
const LEFT_ALIGNED: SortKey[] = ['name', 'kind'];

/** The action cells after the sortable columns: Move, Rename, Delete. */
const ACTION_COLUMNS = 3;

/**
 * Why the one protected folder cannot be renamed. Said where the action is,
 * rather than the action being hidden: a row with one button fewer than its
 * neighbours would leave the reader guessing (FR-004).
 */

/**
 * Splits a filename so the end of it survives truncation.
 *
 * Clipping only at the end removed exactly the part that tells two files
 * apart: `product_x_next_PRD.md` and `product_x_next_notes.md` both render as
 * `product_x_n…`, extension included. The head flexes and truncates; the tail
 * is always drawn.
 */
const NAME_TAIL_LENGTH = 8;

function splitName(name: string): { head: string; tail: string } {
  if (name.length <= NAME_TAIL_LENGTH) return { head: '', tail: name };
  return {
    head: name.slice(0, name.length - NAME_TAIL_LENGTH),
    tail: name.slice(name.length - NAME_TAIL_LENGTH),
  };
}

/** The folder containing `path`, or `undefined` at the root. */
function parentOf(path: string): string | undefined {
  if (path === '') return undefined;
  const cut = path.lastIndexOf('/');
  return cut === -1 ? '' : path.slice(0, cut);
}

/**
 * One entry in the listing.
 *
 * A component rather than JSX inside a `.map()` because dragging needs a hook
 * per row, and hooks cannot be called from a loop body whose length changes as
 * the listing does.
 */
function FileRow({
  entry,
  index,
  rowIndex,
  rowHeight,
  offset,
  isActive,
  isSelected,
  isMoving,
  onActivateIndex,
  onKeyDown,
  onActivate,
  onRequestMove,
  onRequestRename,
  onRequestDelete,
}: {
  entry: FileEntry;
  index: number;
  rowIndex: number;
  rowHeight: number;
  offset: number;
  isActive: boolean;
  isSelected: boolean;
  isMoving: boolean;
  onActivateIndex: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onActivate: () => void;
  onRequestMove?: (entry: FileEntry) => void;
  onRequestRename?: (entry: FileEntry) => void;
  /** Absent for someone who may only read the workspace (spec 189). */
  onRequestDelete?: (entry: FileEntry) => void;
}) {
  const isDirectory = entry.kind === 'directory';
  const isPhone = usePhoneWidth();
  const protectedReasonId = `${entry.path}-protected`;
  // Why this row's folder must keep its name and place, if it must.
  const reason = protectionReason(entry);
  const drag = useRowDragBinding();
  const draggable = drag.useRowDraggable(entry.path);
  // A folder is somewhere an item can go; a file is not, which is why a file
  // row simply never becomes a drop target (FR-031).
  const droppable = drag.useRowDroppable(
    isDirectory ? { kind: 'folder', path: entry.path } : undefined,
  );

  /**
   * Both roles share this element — a row is something to pick up and, if it is
   * a folder, somewhere to put something down.
   *
   * Memoized, and that is not a micro-optimisation. React calls a ref callback
   * with `null` and then the node again whenever the callback's identity
   * changes, so an inline arrow here detaches and re-registers the node on
   * *every* re-render. A row re-renders constantly while a drag is in progress
   * — hover state changes on each pointer move — which had dnd-kit re-measuring
   * a node mid-drag and placing the drag overlay at the row's offset inside the
   * virtualizer's sizer rather than at its position on screen: the overlay
   * appeared far above the pointer, by exactly the row's `translateY`.
   */
  const { setNodeRef: setDraggableNode } = draggable;
  const { setNodeRef: setDroppableNode } = droppable;
  const setRowRef = useCallback(
    (element: HTMLElement | null) => {
      setDraggableNode?.(element);
      setDroppableNode?.(element);
    },
    [setDraggableNode, setDroppableNode],
  );

  return (
    <div
      // The drag bindings go on first, deliberately. They carry accessibility
      // attributes of their own — the library assumes it is decorating a
      // standalone drag handle — and a row is not a handle: it is a
      // `role="row"` in an ARIA grid where one row at a time holds the tab
      // stop. Spreading them last let `role="button"` and a fixed `tabIndex`
      // win, which broke grid semantics and roving-tabindex navigation for
      // every row the moment the real drag layer was mounted.
      {...(isMoving || !canDrag(entry) ? {} : draggable.handleProps)}
      role="row"
      aria-rowindex={rowIndex}
      aria-selected={isSelected}
      data-row-index={index}
      data-drop-state={
        droppable.isOver
          ? 'accepting'
          : droppable.refusing
            ? 'refusing'
            : undefined
      }
      ref={setRowRef}
      tabIndex={isActive ? 0 : -1}
      onFocus={() => {
        onActivateIndex();
      }}
      onKeyDown={(event) => {
        onKeyDown(event);
      }}
      onClick={() => onActivate()}
      className={cn(
        'group focus-visible:ring-ring absolute left-0 flex w-full cursor-default items-center border-l-2 border-transparent text-sm transition-colors focus-visible:ring-3 focus-visible:outline-none focus-visible:-outline-offset-2',
        'hover:bg-accent/60',
        isSelected && 'bg-accent border-l-primary',
        draggable.isDragging && 'opacity-50',
        // Shown while hovering, before release — the reader should not have to
        // try a move to find out it is impossible (FR-030/FR-032).
        droppable.isOver && 'bg-accent ring-primary ring-2 ring-inset',
        droppable.refusing && 'ring-destructive ring-2 ring-inset',
      )}
      style={{
        height: rowHeight,
        // `top`, not `transform: translateY(...)`.
        //
        // Virtualizers normally place rows with a transform because it is the
        // cheaper property to animate, and that is what this list did. But
        // dnd-kit measures a dragged node *transform-agnostically* — it strips
        // the element's own CSS transform, because during a drag it is itself
        // translating that node and needs measurements that do not compound.
        // It cannot tell the virtualizer's offset apart from its own, so it
        // stripped that too and reported every row as sitting `offset` pixels
        // higher than it really was. The drag overlay then appeared that far
        // above the pointer — 180px for the sixth row, and further down the
        // list the worse it got.
        //
        // Nothing here is animated, so `top` costs nothing and leaves dnd-kit
        // with an honest rectangle to measure.
        top: offset,
      }}
    >
      <div
        role="gridcell"
        aria-colindex={1}
        className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden px-3 text-left"
      >
        {isDirectory ? (
          <FolderIcon
            aria-hidden="true"
            className="text-muted-foreground size-4 shrink-0"
            strokeWidth={1.75}
          />
        ) : (
          <FileIcon
            aria-hidden="true"
            className="text-muted-foreground size-4 shrink-0"
            strokeWidth={1.75}
          />
        )}
        {/* The name travels into the preview's header when this file is
            opened. Only one element may carry a transition name per frame, and
            this list stays mounted behind the preview — so the row yields the
            name to the pane that is about to show it, exactly as a task card
            yields to its drawer. */}
        <span
          className="flex min-w-0"
          title={entry.name}
          style={
            isSelected
              ? undefined
              : { viewTransitionName: transitionName(entry.path) }
          }
        >
          <span className="truncate">{splitName(entry.name).head}</span>
          <span data-testid="file-name-tail" className="shrink-0">
            {splitName(entry.name).tail}
          </span>
        </span>
      </div>
      <div
        role="gridcell"
        aria-colindex={2}
        className={cn(
          'text-muted-foreground w-20 shrink-0 truncate px-3 text-xs',
          NOT_ON_PHONE,
        )}
      >
        {classifyFileEntry(entry).label}
      </div>
      <div
        role="gridcell"
        aria-colindex={3}
        className={cn(
          'text-muted-foreground w-16 shrink-0 pr-3 text-right text-xs',
          NOT_ON_PHONE,
        )}
      >
        {isDirectory ? '—' : formatFileSize(entry.size)}
      </div>
      <div
        role="gridcell"
        aria-colindex={4}
        className="text-muted-foreground w-20 shrink-0 pr-3 text-right text-xs max-md:w-auto max-md:whitespace-nowrap"
        title={formatExactTime(entry.modified_at)}
      >
        {formatRelativeTime(entry.modified_at)}
      </div>
      <div
        role="gridcell"
        aria-colindex={5}
        className="flex w-8 shrink-0 items-center justify-center max-md:hidden"
      >
        {/*
          The reason, once per row, for both disabled buttons to point at —
          whichever of Move and Rename the page offers.
        */}
        {reason !== undefined ? (
          <span id={protectedReasonId} className="sr-only">
            {reason}
          </span>
        ) : null}
        {onRequestMove !== undefined &&
          !isMoving &&
          (reason !== undefined ? (
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <button
                  type="button"
                  tabIndex={-1}
                  disabled
                  aria-label={`Move ${entry.name}`}
                  aria-describedby={protectedReasonId}
                  className="text-muted-foreground inline-flex items-center justify-center rounded p-1 opacity-0 pointer-coarse:h-11 pointer-coarse:w-8 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100 disabled:opacity-40 group-hover:disabled:opacity-40"
                >
                  <FolderInputIcon aria-hidden="true" className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{reason}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              tabIndex={-1}
              aria-label={`Move ${entry.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onRequestMove(entry);
              }}
              className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-1 opacity-0 pointer-coarse:h-11 pointer-coarse:w-8 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100"
            >
              <FolderInputIcon aria-hidden="true" className="size-4" />
            </button>
          ))}
      </div>
      <div
        role="gridcell"
        aria-colindex={6}
        className="flex w-8 shrink-0 items-center justify-center max-md:hidden"
      >
        {onRequestRename !== undefined &&
          !isMoving &&
          (reason !== undefined ? (
            // A disabled button emits no pointer events, so the span around it
            // is what the tooltip listens to.
            <Tooltip>
              <TooltipTrigger render={<span className="inline-flex" />}>
                <button
                  type="button"
                  tabIndex={-1}
                  disabled
                  aria-label={`Rename ${entry.name}`}
                  aria-describedby={protectedReasonId}
                  className="text-muted-foreground inline-flex items-center justify-center rounded p-1 opacity-0 pointer-coarse:h-11 pointer-coarse:w-8 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100 disabled:opacity-40 group-hover:disabled:opacity-40"
                >
                  <PencilLineIcon aria-hidden="true" className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{reason}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              tabIndex={-1}
              aria-label={`Rename ${entry.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onRequestRename(entry);
              }}
              className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-1 opacity-0 pointer-coarse:h-11 pointer-coarse:w-8 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100"
            >
              <PencilLineIcon aria-hidden="true" className="size-4" />
            </button>
          ))}
      </div>
      <div
        role="gridcell"
        aria-colindex={7}
        className="flex w-8 shrink-0 items-center justify-center max-md:w-11"
      >
        {isPhone && !isMoving ? (
          // One ⋯ for the row's three actions on a phone (spec 263). The
          // sheet is portalled, but its events still travel the React tree:
          // stopped here, a tap in it neither opens the row nor lifts it.
          <span
            className="contents"
            onClick={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
          >
            <ActionSheet
              name={entry.name}
              description={reason}
              actions={[
                ...(onRequestMove
                  ? [
                      {
                        label: 'Move',
                        icon: FolderInputIcon,
                        disabled: reason !== undefined,
                        onSelect: () => {
                          onRequestMove(entry);
                        },
                      },
                    ]
                  : []),
                ...(onRequestRename
                  ? [
                      {
                        label: 'Rename',
                        icon: PencilLineIcon,
                        disabled: reason !== undefined,
                        onSelect: () => {
                          onRequestRename(entry);
                        },
                      },
                    ]
                  : []),
                ...(onRequestDelete
                  ? [
                      {
                        label: 'Delete',
                        icon: Trash2Icon,
                        destructive: true,
                        onSelect: () => {
                          onRequestDelete(entry);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </span>
        ) : isMoving ? (
          <Loader2Icon
            aria-label={`Moving ${entry.name}`}
            className="text-muted-foreground size-4 animate-spin"
          />
        ) : onRequestDelete === undefined ? null : (
          // Out of the tab order on purpose: the row is the tab
          // stop, and Delete on the focused row is the keyboard
          // path to the same confirmation.
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Delete ${entry.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onRequestDelete(entry);
            }}
            className="text-muted-foreground hover:text-destructive inline-flex items-center justify-center rounded p-1 opacity-0 pointer-coarse:h-11 pointer-coarse:w-8 group-hover:opacity-100 group-focus-visible:opacity-100 pointer-coarse:opacity-100"
          >
            <Trash2Icon aria-hidden="true" className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Virtualized directory listing with sortable columns, worked by pointer or
 * by keyboard.
 *
 * A `grid` rather than a list: rows carry columns and an action, which a
 * `listbox` may not, and only a window of them exists in the DOM at any
 * moment — so the row count and each row's position have to be stated
 * (`aria-rowcount`/`aria-rowindex`) rather than counted from what is rendered.
 *
 * One row is in the tab order at a time (roving `tabindex`) and the arrow keys
 * move it, which is what makes a file browser feel like one: every row used to
 * be its own tab stop, so reaching the last file in a folder of two hundred
 * meant two hundred presses of Tab.
 *
 * Sort key/direction are local state, not route search params (research.md
 * R10) — resorting the same listing is view state, not something worth
 * putting in the URL. A folder row navigates (changes `path`); a file row
 * selects (changes `file`) — the two search-param updates the caller passes
 * in as `onNavigate`/`onSelectFile`.
 */
export function FileListPane({
  workspaceId,
  currentPath,
  entries,
  selectedPath,
  sort,
  onSortChange,
  onNavigate,
  onRequestMove,
  onRequestRename,
  pendingMovePaths,
  onSelectFile,
  onFileDeleted,
}: {
  workspaceId: string;
  currentPath: string;
  entries: FileEntry[];
  selectedPath: string | undefined;
  sort: FileSort;
  onSortChange: (sort: FileSort) => void;
  onNavigate: (path: string) => void;
  /** Opens the destination picker for this entry. */
  onRequestMove?: (entry: FileEntry) => void;
  /** Opens the rename dialog for this entry. */
  onRequestRename?: (entry: FileEntry) => void;
  /** Entries with a move or rename in flight: shown as in progress and not actionable. */
  pendingMovePaths?: ReadonlySet<string>;
  onSelectFile: (path: string) => void;
  onFileDeleted: (path: string) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<FileEntry | undefined>(
    undefined,
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const deleteFile = useDeleteWorkspaceFile(workspaceId, currentPath);
  const canWork = useCanWork();
  const deleteFolder = useDeleteWorkspaceFolder(workspaceId, currentPath);
  const pendingFolder =
    pendingDelete?.kind === 'directory' ? pendingDelete.path : undefined;
  const { filePaths, isCounting, countFailed } = useFolderContents(
    workspaceId,
    pendingFolder,
  );

  // Memoized because the whole listing is re-sorted otherwise on every render,
  // including the ones an arrow key causes — a copy and an O(n log n) sort per
  // keystroke in a folder of thousands.
  const sorted = useMemo(
    () => sortFileEntries(entries, sort.key, sort.direction),
    [entries, sort.key, sort.direction],
  );

  // Clamped rather than stored clamped: the row count changes underneath this
  // (a delete, a filter, a folder change), and an index left pointing past the
  // end marks no row as the grid's tab stop, which drops the whole listing out
  // of the tab order until someone clicks it.
  /**
   * The `..` row exists only below the root (FR-012), and when it does it takes
   * keyboard index -1 — one before the first entry. Keeping it outside the
   * entries array means every array-driven feature (sorting, typeahead,
   * selection, delete) excludes it without needing to know it exists.
   */
  const hasParentRow = currentPath !== '';
  const firstIndex = hasParentRow ? -1 : 0;
  const activeRow = Math.max(
    firstIndex,
    Math.min(activeIndex, Math.max(sorted.length - 1, firstIndex)),
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const rowHeight = useCoarsePointer() ? TOUCH_ROW_HEIGHT : ROW_HEIGHT;
  // The row to focus once it has been rendered — a row further down the
  // listing than the virtualizer's current window has no DOM node to focus
  // until after `scrollToIndex` brings it into range.
  const pendingFocusRef = useRef<number | null>(null);
  const typeaheadRef = useRef({ prefix: '', at: 0 });
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual's returned functions can't be safely memoized by the React Compiler; this is expected.
  const virtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
  });
  // A pointer that changes (a tablet's keyboard detached) changes every row.
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight]);

  useEffect(() => {
    const index = pendingFocusRef.current;
    if (index === null) return;
    // Searched from the document rather than the scroll container: the `..` row
    // is pinned outside it, so a container-scoped lookup would never find it.
    const row = document.querySelector<HTMLElement>(
      `[data-row-index="${String(index)}"]`,
    );
    if (row) {
      row.focus();
      pendingFocusRef.current = null;
    }
  });

  /** Same column flips the direction; a new column starts ascending. */
  function toggleSort(key: SortKey) {
    onSortChange(
      key === sort.key
        ? { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  }

  function moveTo(index: number) {
    if (sorted.length === 0 && !hasParentRow) return;
    const clamped = Math.max(
      firstIndex,
      Math.min(index, Math.max(sorted.length - 1, firstIndex)),
    );
    setActiveIndex(clamped);
    pendingFocusRef.current = clamped;
    // The parent row is pinned outside the virtualizer, so there is nothing to
    // scroll to for it.
    if (clamped >= 0) virtualizer.scrollToIndex(clamped);
  }

  /** Jumps to the next row whose name starts with what has just been typed. */
  function jumpTo(character: string) {
    const now = Date.now();
    const previous = typeaheadRef.current;
    const prefix =
      now - previous.at < TYPEAHEAD_WINDOW_MS
        ? previous.prefix + character
        : character;
    typeaheadRef.current = { prefix, at: now };

    const needle = prefix.toLowerCase();
    // A fresh prefix looks past the current row so repeating a letter walks
    // through the rows that start with it; a growing one re-tests the row it
    // already landed on.
    const from = activeRow + (prefix.length === 1 ? 1 : 0);
    for (let step = 0; step < sorted.length; step += 1) {
      const index = (from + step) % sorted.length;
      if (sorted[index].name.toLowerCase().startsWith(needle)) {
        moveTo(index);
        return;
      }
    }
  }

  /**
   * The `..` row's own keys. It answers to movement and activation and to
   * nothing else — no Delete, no typeahead, no selection.
   */
  function onParentRowKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const parent = parentOf(currentPath);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        return moveTo(0);
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        return moveTo(sorted.length - 1);
      case 'Enter':
      case ' ':
      case 'ArrowLeft':
        if (parent === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        return onNavigate(parent);
      default:
        return;
    }
  }

  function activate(entry: FileEntry) {
    if (entry.kind === 'directory') {
      onNavigate(entry.path);
    } else {
      onSelectFile(entry.path);
    }
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number) {
    const entry = sorted[index];
    const handled = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    switch (event.key) {
      case 'ArrowDown':
        handled();
        return moveTo(index + 1);
      case 'ArrowUp':
        handled();
        return moveTo(index - 1);
      case 'Home':
        handled();
        return moveTo(firstIndex);
      case 'End':
        handled();
        return moveTo(sorted.length - 1);
      case 'Enter':
      case ' ':
        handled();
        return activate(entry);
      case 'ArrowRight':
        if (entry.kind === 'directory') {
          handled();
          onNavigate(entry.path);
        }
        return;
      case 'ArrowLeft': {
        const parent = parentOf(currentPath);
        if (parent !== undefined) {
          handled();
          onNavigate(parent);
        }
        return;
      }
      case 'Delete':
        if (!canWork) return;
        if (pendingMovePaths?.has(entry.path) ?? false) return;
        handled();
        setPendingDelete(entry);
        return;
      case 'F2':
        // The keyboard path to the row's Rename button, which is out of the
        // tab order for the same reason Delete's is.
        if (onRequestRename === undefined) return;
        if (pendingMovePaths?.has(entry.path) ?? false) return;
        if (isProtectedFolder(entry)) return;
        handled();
        onRequestRename(entry);
        return;
      default:
        if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey
        ) {
          handled();
          jumpTo(event.key);
        }
    }
  }

  // A delete stays in its dialog until it resolves: success closes it and
  // says so, a refusal keeps it open with the reason (spec 129 FR-005).
  function confirmDelete() {
    if (!pendingDelete) return;
    const { path: deletedPath, name } = pendingDelete;
    deleteFile.mutate(
      { path: deletedPath, fileName: name },
      {
        onSuccess: () => {
          setPendingDelete(undefined);
          toast.success(`Deleted ${name}.`);
          onFileDeleted(deletedPath);
        },
      },
    );
  }

  function confirmFolderDelete() {
    if (!pendingDelete) return;
    const { path: deletedPath, name } = pendingDelete;
    deleteFolder.mutate(
      { folderPath: deletedPath, folderName: name },
      {
        onSuccess: () => {
          setPendingDelete(undefined);
          toast.success(`Deleted ${name}.`);
          // Anything open from inside the folder goes with it.
          onFileDeleted(deletedPath);
        },
      },
    );
  }

  function cancelDelete() {
    setPendingDelete(undefined);
    // A refusal belongs to the delete that drew it; the next one starts clean.
    deleteFile.reset();
    deleteFolder.reset();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        role="grid"
        aria-label="Files"
        // Stated, not counted: only a window of rows is ever in the DOM. The
        // header row counts as the first, and the `..` row — pinned outside the
        // virtualizer — as the second when it is present (FR-017).
        aria-rowcount={sorted.length + 1 + (hasParentRow ? 1 : 0)}
        aria-colcount={COLUMNS.length + ACTION_COLUMNS}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div role="rowgroup" className="flex-none">
          <div
            role="row"
            aria-rowindex={1}
            className="border-border text-muted-foreground flex items-center border-b text-xs font-medium"
          >
            {COLUMNS.map((column, columnIndex) => (
              <div
                key={column.key}
                role="columnheader"
                aria-colindex={columnIndex + 1}
                aria-sort={
                  sort.key === column.key
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
                className={cn(
                  column.width,
                  column.key === 'name' ? 'text-left' : 'shrink-0',
                  column.key === 'kind' && 'text-left',
                  column.phone === false && NOT_ON_PHONE,
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(column.key)}
                  className={cn(
                    'hover:text-foreground focus-visible:ring-ring flex w-full items-center gap-1 px-3 py-2 focus-visible:ring-3 focus-visible:outline-none pointer-coarse:min-h-11',
                    LEFT_ALIGNED.includes(column.key)
                      ? 'text-left'
                      : 'justify-end',
                    sort.key === column.key && 'text-foreground',
                  )}
                >
                  {column.label}
                  {sort.key === column.key &&
                    (sort.direction === 'asc' ? (
                      <ArrowUpIcon aria-hidden="true" className="size-3" />
                    ) : (
                      <ArrowDownIcon aria-hidden="true" className="size-3" />
                    ))}
                </button>
              </div>
            ))}
            <div
              role="columnheader"
              aria-colindex={COLUMNS.length + 1}
              className="w-8 shrink-0 max-md:hidden"
            >
              <span className="sr-only">Move</span>
            </div>
            <div
              role="columnheader"
              aria-colindex={COLUMNS.length + 2}
              className="w-8 shrink-0 max-md:hidden"
            >
              <span className="sr-only">Rename</span>
            </div>
            <div
              role="columnheader"
              aria-colindex={COLUMNS.length + 3}
              className="w-8 shrink-0 max-md:w-11"
            >
              <span className="sr-only">Actions</span>
            </div>
          </div>
        </div>

        {hasParentRow && (
          <div role="rowgroup" className="flex-none">
            <ParentDirectoryRow
              active={activeRow === -1}
              parentPath={parentOf(currentPath)}
              onFocus={() => {
                setActiveIndex(-1);
              }}
              onKeyDown={onParentRowKeyDown}
              onActivate={() => {
                const parent = parentOf(currentPath);
                if (parent !== undefined) onNavigate(parent);
              }}
            />
          </div>
        )}

        <div
          ref={parentRef}
          role="rowgroup"
          className="min-h-0 flex-1 overflow-auto"
        >
          {/* Presentational: the sizer exists to give the scroll container the
              full listing's height, and must not stand between the rowgroup
              and its rows. */}
          <div
            role="presentation"
            className="relative w-full"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const entry = sorted[virtualRow.index];
              return (
                <FileRow
                  key={entry.path}
                  entry={entry}
                  index={virtualRow.index}
                  rowIndex={virtualRow.index + 2 + (hasParentRow ? 1 : 0)}
                  rowHeight={virtualRow.size}
                  offset={virtualRow.start}
                  isActive={virtualRow.index === activeRow}
                  isSelected={selectedPath === entry.path}
                  // No optimistic removal: the row stays where it is, visibly
                  // in progress, until the outcome is known (FR-025a). Showing
                  // the result early would mean showing one that can still be
                  // wrong.
                  isMoving={pendingMovePaths?.has(entry.path) ?? false}
                  onActivateIndex={() => {
                    setActiveIndex(virtualRow.index);
                  }}
                  onKeyDown={(event) => {
                    onRowKeyDown(event, virtualRow.index);
                  }}
                  onActivate={() => {
                    activate(entry);
                  }}
                  onRequestMove={onRequestMove}
                  onRequestRename={onRequestRename}
                  onRequestDelete={canWork ? setPendingDelete : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>

      <DeleteFileDialog
        fileName={pendingDelete?.name ?? ''}
        open={pendingDelete?.kind === 'file'}
        isPending={deleteFile.isPending}
        error={deleteFile.error?.message ?? null}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <DeleteFolderDialog
        folderName={pendingDelete?.name ?? ''}
        fileCount={filePaths?.length}
        isCounting={isCounting}
        countFailed={countFailed}
        isDeleting={deleteFolder.isPending}
        error={deleteFolder.error?.message ?? null}
        open={pendingDelete?.kind === 'directory'}
        onConfirm={confirmFolderDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}
